// Web-реализация фабрики PowerSync-БД: WASM SQLite, worker-режим по умолчанию.
//
// Парная реализация для Capacitor — `apps/mobile/src/powersync-native-db.ts`
// (нативный SQLite через @powersync/capacitor). Выбор делает Vite-алиас
// `@powersync-impl` по режиму сборки, поэтому `client.ts` остаётся синхронным и
// ничего не знает о платформе.
//
// Режимы (db-mode.ts):
//   • worker (дефолт) — SDK-дефолты v2: SQLite в dedicated worker (главный
//     поток не блокируется записью синка), синк в SharedWorker — ОДНА
//     sync-сессия на все вкладки (multi-tab; на Safari/мобильных SDK сам
//     отключает SharedWorker и остаётся per-tab). VFS — IndexedDB (дефолт v2),
//     OPFS не используется.
//   • main-thread — прежний safe-режим (useWebWorker:false, enableMultiTabs:
//     false): БД на главном потоке, per-tab синк. Сюда уводит watchdog.
//
// История: инцидент 4c88c67b (июнь 2026) — на SDK v1 worker грузился по
// «сырому» UMD-URL и не поднимался (OPFS-lock + перехват PWA SW) → БД не
// открывалась вовсе. В v2 worker-скрипты бандлятся Vite как обычные ассеты
// (`new URL('./worker.js', import.meta.url)`), а дефолтный VFS — IndexedDB.
// Тем не менее страховка обязательна: если `waitForReady()` не разрешился за
// WORKER_READY_TIMEOUT_MS (или отклонился), помечаем worker-режим сломанным на
// этом билде, шлём событие в Hawk и перезагружаем страницу — второй заход
// откроет БД в main-thread-режиме без watchdog.
//
// Цикл перезагрузок невозможен РОВНО пока решение записалось: `readPowerSyncDbMode`
// на следующем заходе видит флаг и watchdog не вешает. Если запись не удалась
// (приватное окно, «блокировать данные сайтов», исчерпанная квота), перезагрузка
// повторяла бы тот же путь каждые ~8 секунд — а условие, из-за которого нечего
// писать, само по себе не проходит. Причём именно оно и ломает открытие БД:
// обоим режимам, worker и main-thread, нужен IndexedDB, поэтому фоллбек в таком
// окружении не помог бы всё равно. Тогда перезагрузку не делаем вовсе: событие в
// Hawk уже ушло, а страница остаётся живой (без локальной БД) вместо мигающей.

import type { CommonPowerSyncDatabase } from "@powersync/common";
import { createConsoleLogger, LogLevels } from "@powersync/common";
import { PowerSyncDatabase } from "@powersync/web";
import { captureClientError } from "@/lib/observability/hawk-client";
import type { CreatePowerSyncDatabase } from "./create-db.js";
import { markWorkerModeBroken, readPowerSyncDbMode } from "./db-mode.js";
import { armIdbConnectionLossGuard } from "./idb-connection-loss.js";

/**
 * Потолок открытия БД в worker-режиме. `waitForReady` — это инициализация
 * схемы локальной SQLite (не первичный синк), на живом устройстве это сотни
 * миллисекунд; 8 секунд оставляют запас на холодную загрузку worker-чанка.
 */
const WORKER_READY_TIMEOUT_MS = 8_000;

/** Пауза перед reload — шанс событию Hawk уйти в коллектор. */
const RELOAD_DELAY_MS = 300;

/**
 * Логгер SDK. По умолчанию PowerSync пишет в консоль с уровня `info` — и
 * обычный пользователь видел у себя служебные строки вроде «calling the last
 * port client provider for credentials» и «Could not apply checkpoint due to
 * local data» (жалоба 2026-08-19). Это внутренняя кухня синка, а не сообщение
 * человеку.
 *
 * В проде поднимаем порог до `warn`: предупреждения и ошибки остаются — по ним
 * разбирают инциденты синка, и глушить их значило бы ослепнуть. В dev остаётся
 * `info`, там подробности синка и нужны.
 */
const syncLogger = createConsoleLogger({
	minLevel: import.meta.env.DEV ? LogLevels.info : LogLevels.warn,
});

export const createPowerSyncDatabase: CreatePowerSyncDatabase = (schema, dbFilename) => {
	const mode = readPowerSyncDbMode();
	// Оба режима держат БД в IndexedDB, и оба теряют её вместе с соединением
	// (возврат Safari из фона) — см. `idb-connection-loss.ts`.
	armIdbConnectionLossGuard(mode);

	if (mode === "main-thread") {
		return new PowerSyncDatabase({
			schema,
			logger: syncLogger,
			database: {
				dbFilename,
				useWebWorker: false,
				enableMultiTabs: false,
			},
		});
	}

	// Worker-режим: флаги НЕ передаём — SDK сам включает worker и multi-tab и
	// сам отключает multi-tab там, где SharedWorker недоступен (Safari, мобильные
	// UA). Жёсткое `enableMultiTabs: true` форсировало бы SharedWorker и там.
	const db = new PowerSyncDatabase({
		schema,
		logger: syncLogger,
		database: { dbFilename },
	});
	attachWorkerWatchdog(db);
	return db;
};

/**
 * Fail-safe worker-режима: не открылась БД → фоллбек на main-thread через
 * reload. Худший сценарий повторения инцидента — «одна перезагрузка и работаем
 * как раньше», а не вечные скелетоны.
 */
function attachWorkerWatchdog(db: CommonPowerSyncDatabase): void {
	if (typeof window === "undefined") return;

	let settled = false;
	const fallback = (reason: string, err?: unknown): void => {
		if (settled) return;
		settled = true;
		// Решение «этот билд — main-thread» живёт в localStorage, и только оно
		// делает перезагрузку осмысленной: без записи следующий заход снова
		// выберет worker-режим и снова упрётся в watchdog — вечная карусель.
		const persisted = markWorkerModeBroken();
		captureClientError(
			err instanceof Error ? err : new Error(`powersync: worker-режим не открыл БД (${reason})`),
			{ scope: "powersync.worker-watchdog", reason, mode_persisted: persisted },
		);
		if (!persisted) return;
		window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
	};

	const timer = window.setTimeout(() => fallback("timeout"), WORKER_READY_TIMEOUT_MS);
	db.waitForReady().then(
		() => {
			settled = true;
			window.clearTimeout(timer);
		},
		(err: unknown) => {
			window.clearTimeout(timer);
			fallback("open-failed", err);
		},
	);
}
